import type { HTMLAttributes, RefObject } from 'react';
import type { ASubheaderProps } from '../../atoms/ASubheader/ASubheader.types';
import type { AIframeProps } from '../../atoms/AIframe/AIframe.types';
import type { AVideoProps } from '../../atoms/AVideo/AVideo.types';

export enum OModalMediaType {
  iFrame = 'iFrame',
  Video = 'Video',
}

export interface OModalMediaI18nProps {
  closeDialog?: string;
}

export interface OModalMediaProps extends HTMLAttributes<HTMLDialogElement> {
  /**
   * Determines which type of media to display. 'iFrame' or 'Video'. Defaults to iFrame.
   */
  type?: OModalMediaType;
  /**
   * Whether to display the subheader section.
   */
  subheader?: boolean;
  /**
   * Props for the subheader component (aSubheader). Size is fixed to L.
   */
  aSubheader?: ASubheaderProps;
  /**
   * Props related to iFrame content.
   */
  aIframe?: AIframeProps;
  /**
   * Props related to video content.
   */
  aVideo?: AVideoProps;
  /** Opens/closes native dialog via showModal()/close(). Defaults closed. */
  isOpen?: boolean;
  /** Called on close button, Esc, dialog close/cancel, or backdrop click. */
  onClose?: () => void;
  /** Refocus trigger on close if native restore fails. */
  triggerRef?: RefObject<HTMLElement>;
  /** i18n overrides; closeDialog sets the close button aria-label. */
  i18nProperties?: OModalMediaI18nProps;
  /**
   * Opt-in portal rendering. When true, renders via createPortal to
   * document.body, escaping ancestor DOM event handlers
   * (e.g. Swiper's onTouchStart suppressing mousedown).
   * @default false
   */
  portal?: boolean;
  /**
   * Video presentation mode.
   * - 'modal': opens in a \<dialog\> (default)
   * - 'native': delegates to platform player / fullscreen API (mobile)
   *
   * NOTE: 'native' requires DUX-3741 (AVideo playsInline + Fullscreen API
   * for Brightcove). Until then, 'native' falls back to 'modal' behavior.
   * @default 'modal'
   */
  playbackMode?: 'modal' | 'native';
}
